import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import Loader2 from '../../components/loader2';
import showToast from '../../components/ShowToast';
import { jwtDecode } from 'jwt-decode';
import OnboardingProgressBar from '../../components/Projects/OnboardingProgressBar';
import './ConfirmContractors.css';

const ConfirmContractors = () => {
    const { templateId } = useParams();
    const [searchParams] = useSearchParams();
    const postcode = searchParams.get('postcode');
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [selectedContractors, setSelectedContractors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [homeUserId, setHomeUserId] = useState(null);
    const [estimation, setEstimation] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        let uid = null;
        if (token) {
            try {
                const decodedToken = jwtDecode(token);
                uid = decodedToken.nameid || localStorage.getItem('loggedInHomeUserId');
            } catch (e) {
                console.warn("JWT decode failed, falling back to storage:", e);
                uid = localStorage.getItem('loggedInHomeUserId');
            }
        }
        if (uid) setHomeUserId(uid);
        if (!uid) {
            setError("No user identified. Please log in.");
            setIsLoading(false);
            showToast('error', 'You must be logged in to create a project.');
            // Optionally redirect to login
            // navigate('/login');
        }
    }, []);

    useEffect(() => {
        if (!templateId || !homeUserId) return;

        const fetchTemplate = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/ProjectTemplates/${templateId}`);
                setTemplate(response.data);
                // Pre-select all suggested contractors by default
                setSelectedContractors(response.data.suggestedContractors.map(c => c.profession?.name || "General"));
                
                // Mock estimation based on template
                // In a real app, this would call an endpoint with postcode and templateId
                generateEstimation(response.data.name);

            } catch (err) {
                setError('Failed to fetch project template details.');
                console.error('Error fetching template:', err);
                showToast('error', 'Failed to load project template details.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTemplate();
    }, [templateId, homeUserId]);

    const generateEstimation = (templateName) => {
        // Simple mock logic for estimation
        let minCost = 5000;
        let maxCost = 10000;
        let weeks = "2-4";

        if (templateName.toLowerCase().includes('kitchen')) {
            minCost = 15000;
            maxCost = 35000;
            weeks = "4-6";
        } else if (templateName.toLowerCase().includes('bathroom')) {
            minCost = 12000;
            maxCost = 25000;
            weeks = "3-5";
        } else if (templateName.toLowerCase().includes('deck')) {
            minCost = 8000;
            maxCost = 18000;
            weeks = "2-3";
        }

        setEstimation({
            range: `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`,
            timeline: `${weeks} Weeks`
        });
    };

    const handleContractorToggle = (professionName) => {
        if (!professionName) return;
        setSelectedContractors(prev =>
            prev.includes(professionName)
                ? prev.filter(name => name !== professionName)
                : [...prev, professionName]
        );
    };

    const handleCreateProject = async () => {
        if (!homeUserId) {
            showToast('error', 'User not identified. Please log in.');
            return;
        }
        if (!template) {
            showToast('error', 'Project template not loaded.');
            return;
        }

        setIsLoading(true);
        try {
            // Call the backend endpoint to create a project from the template
            const response = await axios.post(
                `${API_BASE_URL}/api/UserProjects/FromTemplate/${templateId}`,
                {
                    userId: Number(homeUserId),
                    userType: 'HomeUser',
                    postcode: postcode // Pass the postcode to the backend
                },
                {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );
            const newProjectId = response.data.projectId;
            showToast('success', 'AI-Assisted Project created successfully!');
            navigate(`/project/${newProjectId}`); // Redirect to the new project's detail page
        } catch (err) {
            setError('Failed to create AI-Assisted Project.');
            console.error('Error creating project from template:', err);
            showToast('error', 'Failed to create project.');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <Loader2 />;
    }

    if (error) {
        return <div className="error-message">{error}</div>;
    }

    if (!template) {
        return <div className="error-message">Project template not found or loaded.</div>;
    }

    return (
        <div className="confirm-contractors-container">
            <OnboardingProgressBar currentStep={3} />

            {estimation && (
                <div className="estimation-card">
                    <div className="estimation-header">
                        <h3>Estimated Project Scope</h3>
                        <span className="estimate-badge">AI Estimate</span>
                    </div>
                    <div className="estimation-details">
                        <div className="estimation-item">
                            <span className="label">Estimated Cost</span>
                            <span className="value">{estimation.range}</span>
                        </div>
                        <div className="estimation-item">
                            <span className="label">Typical Timeline</span>
                            <span className="value">{estimation.timeline}</span>
                        </div>
                    </div>
                    <p className="disclaimer">* This is a rough estimate based on typical projects in {postcode || 'your area'}. Actual costs may vary.</p>
                </div>
            )}

            <h2>Project: {template.name}</h2>
            <p className="subtitle">Suggested Professionals</p>
            <p>We've pre-selected the typical trades needed for this project. Uncheck any you don't need.</p>

            <div className="contractor-list">
                {template.suggestedContractors.map((contractor, index) => {
                    const name = contractor.profession?.name || "General";
                    return (
                        <div key={index} className={`contractor-item ${selectedContractors.includes(name) ? 'selected' : ''}`}
                             onClick={() => handleContractorToggle(name)}>
                            <input
                                type="checkbox"
                                checked={selectedContractors.includes(name)}
                                onChange={() => handleContractorToggle(name)}
                                className="contractor-checkbox"
                            />
                            <span>{name}</span>
                        </div>
                    );
                })}
            </div>

            <div className="action-buttons">
                <button className="nav-button secondary" onClick={() => navigate(-1)}>Back</button>
                <button className="create-project-button" onClick={handleCreateProject} disabled={isLoading}>
                    {isLoading ? 'Creating Project...' : 'Create My AI-Assisted Project'}
                </button>
            </div>
        </div>
    );
};

export default ConfirmContractors;
